import type { SvgIconComponent } from '@mui/icons-material';
import AddTaskIcon from '@mui/icons-material/AddTask';
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import HistoryIcon from '@mui/icons-material/History';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import type {
  DependencyPriority,
  EscalationSeverity,
  IMyDependencySummary,
  IMyEscalationSummary,
  IMyIssueSummary,
  IMyRiskSummary,
  IssuePriority,
  RiskImpact,
  WorkflowEntityType,
} from '@pulse/shared';

// The Home panel's tab set. Adding a kind means one entry in HOME_TAB_CONFIG, one adapter
// below, and one HOME_TABS row — MyWorkPanel/WorkItemRow/WorkItemCard never change.
export type HomeTabKind = 'issue' | 'risk' | 'dependency' | 'escalation';

export type BadgeColor = 'error' | 'warning' | 'info' | 'default';

/** What a Home row actually renders, normalized across every work-item type. Each tab's API
 *  response is adapted into this shape so MyWorkPanel/WorkItemRow/WorkItemCard stay generic
 *  and none of them needs to know what an Issue or a Risk is. */
export interface HomeWorkRow {
  id: string;
  kind: HomeTabKind;
  /** issueKey | riskKey — the human-readable, project-prefixed identifier. */
  itemKey: string;
  /** Issue.name | Risk.title. */
  title: string;
  projectId: string;
  projectName: string;
  projectKey: string;
  /** Workflow step name. Never compared against a string literal — see HomeTabConfig. */
  status: string;
  /** The kind's severity-ish chip: Priority for issues, Impact for risks. */
  badge?: { label: string; color: BadgeColor };
  /** ISO 8601 date. Issue.dueDate | Risk.targetResolutionDate. */
  dueDate?: string;
  /** Issue-only fields, namespaced rather than spread as four loose optionals so a single
   *  `row.issue &&` guard gates the whole expedited/flag/hours/comments block — and so nothing
   *  can accidentally read `commentCount` off a Risk. */
  issue?: {
    flagged: boolean;
    expedited: boolean;
    totalLoggedHours: number;
    commentCount: number;
  };
}

// Issue Priority, Dependency Priority and Escalation Severity are the same four levels under
// three names, so one colour map serves all three badges rather than three identical copies.
type UrgencyLevel = IssuePriority | DependencyPriority | EscalationSeverity;

export const URGENCY_COLOR: Record<UrgencyLevel, BadgeColor> = {
  Critical: 'error', High: 'error', Medium: 'warning', Low: 'info',
};

// Mirrors ProjectRisksTab.tsx's own IMPACT_COLOR so a risk's impact chip reads the same colour
// on Home as it does on the project's Risks tab. Kept as a local copy rather than exported from
// that tab component — a types module importing from a 400-line page component is the wrong
// direction of dependency.
export const IMPACT_COLOR: Record<RiskImpact, BadgeColor> = {
  Severe: 'error', Major: 'error', Moderate: 'warning', Minor: 'default', Negligible: 'default',
};

export function issueToRow(issue: IMyIssueSummary): HomeWorkRow {
  return {
    id: issue.id,
    kind: 'issue',
    itemKey: issue.issueKey,
    title: issue.name,
    projectId: issue.projectId,
    projectName: issue.projectName,
    projectKey: issue.projectKey,
    status: issue.status,
    badge: issue.priority ? { label: issue.priority, color: URGENCY_COLOR[issue.priority] } : undefined,
    dueDate: issue.dueDate ?? undefined,
    issue: {
      flagged: issue.flagged,
      expedited: issue.expedited,
      totalLoggedHours: issue.totalLoggedHours,
      commentCount: issue.commentCount,
    },
  };
}

export function riskToRow(risk: IMyRiskSummary): HomeWorkRow {
  return {
    id: risk.id,
    kind: 'risk',
    itemKey: risk.riskKey,
    title: risk.title,
    projectId: risk.projectId,
    projectName: risk.projectName,
    projectKey: risk.projectKey,
    status: risk.status,
    badge: { label: risk.impact, color: IMPACT_COLOR[risk.impact] ?? 'default' },
    dueDate: risk.targetResolutionDate,
    // No `issue` block — a risk has no expedited flag, work log, or comment thread, so the
    // row's bolt/hours/comment affordances don't render for it.
  };
}

export function dependencyToRow(dependency: IMyDependencySummary): HomeWorkRow {
  return {
    id: dependency.id,
    kind: 'dependency',
    itemKey: dependency.dependencyKey,
    title: dependency.title,
    projectId: dependency.projectId,
    projectName: dependency.projectName,
    projectKey: dependency.projectKey,
    status: dependency.status,
    badge: dependency.priority ? { label: dependency.priority, color: URGENCY_COLOR[dependency.priority] } : undefined,
    dueDate: dependency.dueDate,
  };
}

export function escalationToRow(escalation: IMyEscalationSummary): HomeWorkRow {
  return {
    id: escalation.id,
    kind: 'escalation',
    itemKey: escalation.escalationKey,
    title: escalation.title,
    projectId: escalation.projectId,
    projectName: escalation.projectName,
    projectKey: escalation.projectKey,
    status: escalation.status,
    badge: { label: escalation.severity, color: URGENCY_COLOR[escalation.severity] },
    dueDate: escalation.targetResolutionDate,
  };
}

/** The trailing button that opens this kind's own form dialog. Carried in the config — rather
 *  than branched on `row.kind` inside the row components — because the tooltip and icon differ
 *  by what the dialog actually shows: Risk and Escalation open onto a Log section, a Dependency
 *  opens the record itself for editing (its dialog has no log). Keeping it here is what lets
 *  WorkItemRow/WorkItemCard stay ignorant of which kind they're rendering. */
export interface HomeFormDialogAction {
  label: string;
  icon: SvgIconComponent;
}

export interface HomeTabConfig {
  kind: HomeTabKind;
  tabLabel: string;
  /** The component itself, not rendered JSX — this is a .ts file. Each one is the icon that
   *  already represents this entity elsewhere in the app (ProjectDetailTabs' own tab strip and
   *  each Project*Tab's empty state), so the same concept reads with the same icon everywhere. */
  icon: SvgIconComponent;
  panelTitle: string;
  panelSubtitle: string;
  /** Subtitle shown instead of `panelSubtitle` when `scope === 'led'` — a Lead/Manager/BU Head
   *  view lists the whole team's work on the caller's projects, not just their own, so "assigned
   *  to you"/"you own" phrasing would misrepresent whose items are shown. */
  panelSubtitleLed: string;
  searchPlaceholder: string;
  /** Feeds useWorkflowDefinition() — the single source of this kind's status names, for the
   *  row's StatusPill dropdown, the Status filter, and Group By → Status. */
  workflowEntityType: WorkflowEntityType;
  /** Whether this kind has Issue's `expedited`/`flagged` boolean columns, which get their own
   *  quick-filter chips. The only thing that differs between tabs' filter rows — statuses
   *  themselves are workflow-driven for every kind. */
  supportsIssueFlags: boolean;
  /** null for a kind with no form dialog to open from Home — only Issue, which uses the
   *  IssueDetailDialog reached through its own comments/log-time affordances instead. */
  formDialogAction: HomeFormDialogAction | null;
  emptyStateText: string;
  noMatchesText: string;
}

// Keyed by kind so `HOME_TAB_CONFIG[activeKind]` is a total lookup — a Record over a literal
// union needs no undefined check, unlike `.find()` or an array index under
// `noUncheckedIndexedAccess`. HOME_TABS below is the render order for the tab strip.
export const HOME_TAB_CONFIG: Record<HomeTabKind, HomeTabConfig> = {
  issue: {
    kind: 'issue',
    tabLabel: 'Tasks',
    icon: AddTaskIcon,
    panelTitle: 'My Tasks',
    panelSubtitle: 'Everything assigned to you, across every project',
    panelSubtitleLed: 'Tasks across the projects you lead',
    searchPlaceholder: 'Search tasks or projects',
    workflowEntityType: 'issue',
    supportsIssueFlags: true,
    formDialogAction: null,
    emptyStateText: 'Nothing assigned to you yet.',
    noMatchesText: 'No tasks match the current filters.',
  },
  risk: {
    kind: 'risk',
    tabLabel: 'Risks',
    icon: WarningAmberIcon,
    panelTitle: 'My Risks',
    panelSubtitle: 'Risks you own, across every project',
    panelSubtitleLed: 'Risks across the projects you lead',
    searchPlaceholder: 'Search risks or projects',
    workflowEntityType: 'risk',
    supportsIssueFlags: false,
    formDialogAction: { label: 'Log', icon: HistoryIcon },
    emptyStateText: 'No risks are assigned to you yet.',
    noMatchesText: 'No risks match the current filters.',
  },
  dependency: {
    kind: 'dependency',
    tabLabel: 'Dependencies',
    icon: ArrowRightAltIcon,
    panelTitle: 'My Dependencies',
    panelSubtitle: 'Dependencies you own, across every project',
    panelSubtitleLed: 'Dependencies across the projects you lead',
    searchPlaceholder: 'Search dependencies or projects',
    workflowEntityType: 'dependency',
    supportsIssueFlags: false,
    // Same icon as the Dependencies tab above — one concept, one icon, per apps/web/CLAUDE.md.
    formDialogAction: { label: 'Open', icon: ArrowRightAltIcon },
    emptyStateText: 'No dependencies are assigned to you yet.',
    noMatchesText: 'No dependencies match the current filters.',
  },
  escalation: {
    kind: 'escalation',
    tabLabel: 'Escalations',
    icon: ReportProblemIcon,
    panelTitle: 'My Escalations',
    // Wording reflects the API's OR scope — see MeService.getMyEscalations.
    panelSubtitle: 'Escalations you own or are escalated to, across every project',
    panelSubtitleLed: 'Escalations across the projects you lead',
    searchPlaceholder: 'Search escalations or projects',
    workflowEntityType: 'escalation',
    supportsIssueFlags: false,
    formDialogAction: { label: 'Log', icon: HistoryIcon },
    emptyStateText: 'No escalations are assigned to you yet.',
    noMatchesText: 'No escalations match the current filters.',
  },
};

/** Left-to-right order of the panel's tab strip. */
export const HOME_TABS: readonly HomeTabConfig[] = [
  HOME_TAB_CONFIG.issue,
  HOME_TAB_CONFIG.risk,
  HOME_TAB_CONFIG.dependency,
  HOME_TAB_CONFIG.escalation,
];
